import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Settings, Bell, Mail, MessageSquare, Star, Send, Megaphone,
  ArrowLeft, Save, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    welcome: true,
    new_message: true,
    new_review: true,
    application_sent: true,
    marketing: false
  });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/api/settings/email-preferences`);
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Errore nel caricamento delle preferenze');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/email-preferences`, preferences);
      toast.success('Preferenze salvate con successo!');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Errore nel salvataggio delle preferenze');
    } finally {
      setSaving(false);
    }
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const emailSettings = [
    {
      key: 'new_message',
      icon: <MessageSquare className="w-5 h-5" />,
      title: 'Nuovi messaggi',
      description: 'Ricevi una notifica quando qualcuno ti invia un messaggio'
    },
    {
      key: 'new_review',
      icon: <Star className="w-5 h-5" />,
      title: 'Nuove recensioni',
      description: 'Ricevi una notifica quando ricevi una nuova recensione',
      professionalOnly: true
    },
    {
      key: 'application_sent',
      icon: <Send className="w-5 h-5" />,
      title: 'Conferma candidature',
      description: 'Ricevi una conferma quando invii una candidatura',
      professionalOnly: true
    },
    {
      key: 'marketing',
      icon: <Megaphone className="w-5 h-5" />,
      title: 'Comunicazioni promozionali',
      description: 'Ricevi aggiornamenti su novità e offerte speciali'
    }
  ];

  // Filter settings based on user type
  const filteredSettings = emailSettings.filter(setting => {
    if (setting.professionalOnly && user?.user_type !== 'professional') {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#344B71]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-slate-600 hover:text-[#344B71]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#344B71]/10 rounded-xl">
              <Settings className="w-6 h-6 text-[#344B71]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#344B71]">Impostazioni</h1>
              <p className="text-slate-600">Gestisci le tue preferenze</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Email Preferences Card */}
        <Card className="shadow-sm">
          <CardHeader className="border-b bg-slate-50/50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#E8A007]/10 rounded-lg">
                <Bell className="w-5 h-5 text-[#E8A007]" />
              </div>
              <div>
                <CardTitle className="text-lg text-[#344B71]">Notifiche Email</CardTitle>
                <CardDescription>
                  Scegli quali email desideri ricevere
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {filteredSettings.map((setting) => (
                <div 
                  key={setting.key}
                  className="flex items-center justify-between p-6 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-slate-100 rounded-lg text-slate-600">
                      {setting.icon}
                    </div>
                    <div>
                      <h3 className="font-medium text-[#344B71]">{setting.title}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">{setting.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences[setting.key]}
                    onCheckedChange={() => togglePreference(setting.key)}
                    className="data-[state=checked]:bg-[#344B71]"
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#344B71] hover:bg-[#2a3d5c] text-white px-8"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salva preferenze
              </>
            )}
          </Button>
        </div>

        {/* Info Box */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm text-blue-800">
            <strong>Nota:</strong> Puoi modificare queste preferenze in qualsiasi momento. 
            Le email di benvenuto e le email transazionali essenziali (come reset password) 
            verranno sempre inviate indipendentemente da queste impostazioni.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
